<template>
  <PageWrap page-title="Notificar visitas">
    <div class="mb-4">
      <b-row>
        <b-col md="4" lg="3">
          <BfAutocomplete
            :value="filter.client"
            data-key="client"
            label="Cliente"
            placeholder="Busca por cliente"
            :fetch-suggestions="searchAutocompleteClient"
            clearable
            @update="updateSearchInput"
            @clear="clearClientSearchField"
          />
        </b-col>
        <b-col md="4" lg="3">
          <BfAutocomplete
            :value="filter.chargeCode"
            data-key="chargeCode"
            label="Código"
            placeholder="Busca por código"
            :fetch-suggestions="searchAutocompleteChargeCode"
            clearable
            @update="updateSearchInput"
            @clear="clearCodeSearchField"
          />
        </b-col>
        <b-col md="4" lg="3">
          <BfInput
            :value="filter.town"
            data-key="town"
            label="Población"
            placeholder="Busca por población"
            @update="updateSearchInput"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6" lg="4" xl="3">
          <BfDatePicker
            :value="filter.dateRange"
            label="Entre fechas"
            data-key="dateRange"
            type="daterange"
            start-placeholder="Desde"
            end-placeholder="Hasta"
            value-format="yyyy-MM-dd"
            @update="updateSearchInput"
          />
        </b-col>

        <b-col class="d-flex align-items-center flex-wrap">
          <BfCheckbox
            :value="isPending"
            data-key="isPending"
            label="Pendientes"
            :bottom-gutter="false"
            :display-label="false"
            class="mr-4 mb-2"
            @update="updatePending"
          />

          <BfCheckbox
            :value="filter.isComplete"
            data-key="isComplete"
            label="Completada"
            :bottom-gutter="false"
            :display-label="false"
            class="mr-4 mb-2"
            @update="updateSearchInput"
          />

          <BfCheckbox
            :value="filter.isNotifiedAgendada"
            data-key="isNotifiedAgendada"
            label="Notificada como agendada"
            :bottom-gutter="false"
            :display-label="false"
            class="mr-4 mb-2"
            @update="updateSearchInput"
          />

          <BfCheckbox
            :value="filter.isNotified"
            data-key="isNotified"
            label="Notificada como completa"
            :bottom-gutter="false"
            :display-label="false"
            class="mb-2"
            @update="updateSearchInput"
          />
        </b-col>
      </b-row>
    </div>

    <b-row>
      <b-col cols="12">
        <LoadingIndicator v-if="loading" />

        <template v-else>
          <BfTable
            :data="tableData"
            :columns="tableColumns"
            cell-class-name="tasaciones-small-cell"
            show-checkbox-column
            :checked-rows="selectedTableRows"
            :header-checkbox-checked="allTableRowsSelected"
            @row-click="tableRowClick"
            @row-checkbox-change="rowCheckboxChange"
            @header-checkbox-change="headerCheckboxChange"
          >
            <i
              slot="completada"
              slot-scope="{row}"
              class="fas fa-sm"
              :title="row.completa ? 'Sí' : 'No'"
              :class="{
                'fa-minus-circle text-secondary': !row.completa,
                'fa-check-circle text-success': row.completa,
              }"
            />

            <span
              slot="codigo"
              slot-scope="{row}"
              class="tasaciones-table-marker"
            >{{row.encargo && row.encargo.codigo}}</span>

            <template slot="fecha" slot-scope="{row}">{{row && tableDateFieldValueFormatter(row.fecha_hora_ini)}}</template>

            <template
              slot="numero_encargo"
              slot-scope="{row}"
            >{{row.encargo && row.encargo.numero_encargo}}</template>

            <template
              slot="cliente"
              slot-scope="{row}"
            >{{row.encargo && row.encargo.empresa_cliente_nombre}}</template>

            <template slot="tasador_visita" slot-scope="{row}">{{row.tasador && row.tasador.clave}}</template>

            <template
              slot="solicitante"
              slot-scope="{row}"
            >{{row.encargo && row.encargo.nombre_solicitante}}</template>

            <template
              slot="fecha_encargo"
              slot-scope="{row}"
            >{{row.encargo && row.encargo.fecha_hora_recepcion_texto}}</template>

            <template
              slot="sucursal"
              slot-scope="{row}"
            >{{row.encargo && row.encargo.sucursal && row.encargo.sucursal.sucursal}}</template>

            <i
              slot="notificada_agenda"
              slot-scope="{row}"
              class="fas fa-sm"
              :title="row.notificada_agenda ? 'Sí' : 'No'"
              :class="{
                'fa-minus-circle text-secondary': !row.notificada_agenda,
                'fa-check-circle text-success': row.notificada_agenda,
              }"
            />

            <i
              slot="notificada_completa"
              slot-scope="{row}"
              class="fas fa-sm"
              :title="row.notificada_completa ? 'Sí' : 'No'"
              :class="{
                'fa-minus-circle text-secondary': !row.notificada_completa,
                'fa-check-circle text-success': row.notificada_completa,
              }"
            />
          </BfTable>

          <div v-if="showTableFooterButton" class="mt-4">
            <BfButton @click="tableFooterButtonClick">{{tableFooterButtonLabel}}</BfButton>
          </div>
        </template>
      </b-col>
    </b-row>
  </PageWrap>
</template>

<script>
/* eslint-disable function-paren-newline */
import qsBinder from '@holamundo.app/vue-router-qs-binder';
import PageWrap from '@/components/PageWrap';
import LoadingIndicator from '@/components/LoadingIndicator';
import { applicantAPI, chargeAPI, visitAPI } from '@/api';
import { get, debounce, moment } from '@/utils';

const FILTER_PROPERTIES = {
  chargeCode: undefined,
  client: undefined,
  dateRange: undefined,
  isComplete: undefined,
  isNotified: false,
  isNotifiedAgendada: undefined,
};

export default {
  name: 'Visits',

  mixins: [
    qsBinder({
      dataKey: 'filter',
      data: ({ query }) =>
        Object.keys(FILTER_PROPERTIES).reduce((acc, key) => {
          const value = get(query, [key], FILTER_PROPERTIES[key]);

          const formattedValue = () => {
            switch (key) {
              case 'isComplete':
              case 'isNotified':
              case 'isNotifiedAgendada':
                return typeof value === 'string' ? value === 'true' : value;
              default:
                return value;
            }
          };

          return {
            ...acc,
            [key]: formattedValue(),
          };
        }, {}),
      didUpdate: ($this) => {
        $this.loadVisitsWithIndicator();
      },
    }),
  ],

  data: () => ({
    loading: false,
    searchChargeCodeOptions: [],
    searchClientOptions: [],
    selectedTableRows: [],
    tableData: [],
    firstTime: true,
    isFirstLoad: true,
    isPending: true,
  }),

  created() {
    this.firstLoad();
  },

  methods: {
    tableDateFieldValueFormatter(value) {
      return value ? moment(value).format('DD/MM/YYYY h:m:s') : undefined;
    },
    updatePending() {
      if (this.isPending) {
        this.isPending = false;
        this.loadVisitsWithIndicator();
      } else {
        this.filter.isComplete = false;
        this.filter.isNotified = false;
        this.filter.isNotifiedAgendada = false;
        this.isPending = true;
        this.firstLoad();
      }
    },
    updateFirstLoadCheck() {
      this.firstLoad();
      this.filter.isComplete = false;
      this.filter.isNotified = false;
      this.filter.isNotifiedAgendada = false;
    },
    async firstLoad() {
      this.loading = true;

      const [from = undefined, to = undefined] = this.filter.dateRange || [];

      const {
        data: { data },
      } = await visitAPI.getAll({
        include: 'tasador,encargo',
        chargeCode: this.searchChargeCodeCalculatedValue,
        clientId: this.searchClientCalculatedValue,
        complete: false,
        notified: 0,
        agendada: false,
        town: this.filter.town,
        ...(from && to ? { startDate: from, endDate: to } : {}),
      });
      this.selectedTableRows = [];
      this.tableData = data;
      this.loading = false;
    },
    async loadVisits() {
      const [from = undefined, to = undefined] = this.filter.dateRange || [];

      const {
        data: { data },
      } = await visitAPI.getAll({
        include: 'tasador,encargo',
        chargeCode: this.searchChargeCodeCalculatedValue,
        clientId: this.searchClientCalculatedValue,
        complete: this.filter.isComplete,
        notified: !!this.filter.isNotified,
        agendada: this.filter.isNotifiedAgendada,
        town: this.filter.town,
        ...(from && to ? { startDate: from, endDate: to } : {}),
      });

      this.selectedTableRows = [];
      this.tableData = data;
    },

    async loadVisitsWithIndicator() {
      this.loading = true;
      try {
        await this.loadVisits();
      } finally {
        this.loading = false;
      }
    },

    async searchAutocompleteClient(query, cb) {
      const {
        data: { data },
      } = await applicantAPI.getAll({
        name: query,
        perPage: 999,
      });

      this.searchClientOptions = data;

      const formatted = data.map(client => ({
        value: client.nombre,
      }));

      cb(formatted);
    },

    async searchAutocompleteChargeCode(query, cb) {
      const {
        data: { data },
      } = await chargeAPI.getAll({
        code: query,
        perPage: 999,
      });

      this.searchChargeCodeOptions = data;

      const formatted = data.map(charge => ({
        value: `${charge.codigo} (${charge.numero_encargo})`,
      }));

      cb(formatted);
    },

    tableRowClick({ column, row }) {
      if (column.property === 'codigo' && row.encargo) {
        const {
          encargo: { id },
        } = row;

        this.$router.push({ name: 'charge', params: { id: `${id}` } });
      }
    },

    rowCheckboxChange({ value }) {
      const currentIndex = this.selectedTableRows.indexOf(value);

      if (currentIndex >= 0) {
        this.$delete(this.selectedTableRows, currentIndex);
      } else {
        this.selectedTableRows = [...this.selectedTableRows, value];
      }
    },

    headerCheckboxChange(value) {
      if (value) {
        this.selectedTableRows = this.tableData.map(c => c.id);
      } else {
        this.selectedTableRows = [];
      }
    },

    async tableFooterButtonClick() {
      const isAgendadaButton = this.tableFooterButtonType === 'agendada';
      const isCompletadaButton = this.tableFooterButtonType === 'completada';

      try {
        const requests = this.selectedTableRows.map(id =>
          visitAPI.update({
            id,
            data: {
              ...(isAgendadaButton ? { notificada_agenda: true } : {}),
              ...(isCompletadaButton ? { notificada_completa: true } : {}),
            },
          })
        );

        const resp = await Promise.all(requests);

        this.$bfNotify({
          type: 'success',
          title: `Actualizada${this.selectedTableRows.length > 1 ? 's' : ''}`,
          message: get(resp, [0, 'message'], ''),
        });

        // this.loadVisits();
        this.firstLoad();
      } catch ({ message = '' }) {
        this.$bfNotify({
          type: 'error',
          title: 'Error',
          message,
        });
      }
    },

    /* eslint-disable-next-line func-names */
    updateSearchInput: debounce(function({ key, value }) {
      this.isPending = false;
      this.updateQsData({
        [key]: value,
      });
    }, 400),

    clearClientSearchField() {
      this.updateQsData({
        client: null,
      });
    },

    clearCodeSearchField() {
      this.updateQsData({
        chargeCode: null,
      });
    },
  },

  computed: {
    searchClientCalculatedValue() {
      const { client } = this.filter;

      if (client && !!this.searchClientOptions.length) {
        const relatedClient = this.searchClientOptions.find(
          c => c.nombre === client
        );

        if (relatedClient && relatedClient.id) {
          return relatedClient.id;
        }
      }

      return undefined;
    },

    searchChargeCodeCalculatedValue() {
      const { chargeCode } = this.filter;

      if (chargeCode && !!this.searchChargeCodeOptions.length) {
        const charge = this.searchChargeCodeOptions.find(
          c => `${c.codigo} (${c.numero_encargo})` === chargeCode
        );

        if (charge && charge.codigo) {
          return charge.codigo;
        }
      }

      return undefined;
    },

    tableHasSelectedRows() {
      return !!this.selectedTableRows.length;
    },

    tableColumns() {
      return [
        {
          label: 'Comp.',
          dataKey: 'completada',
          width: 90,
          align: 'center',
          sortable: true,
        },
        {
          label: 'Cod.',
          dataKey: 'codigo',
          width: 80,
          sortable: true,
        },
        {
          label: 'F. Visita',
          dataKey: 'fecha',
          width: 160,
          sortable: true,
        },
        {
          label: 'Nº Encargo',
          dataKey: 'numero_encargo',
          width: 120,
          sortable: true,
        },
        {
          label: 'Cliente',
          dataKey: 'cliente',
          minWidth: 100,
          sortable: true,
        },
        {
          label: 'Tas. Visita',
          dataKey: 'tasador_visita',
          width: 110,
          sortable: true,
        },
        {
          label: 'Solicitante',
          dataKey: 'solicitante',
          minWidth: 180,
          sortable: true,
        },
        {
          label: 'Fecha encargo',
          dataKey: 'fecha_encargo',
          width: 150,
          sortable: true,
        },
        {
          label: 'Sucursal',
          dataKey: 'sucursal',
          minWidth: 90,
          sortable: true,
        },
        {
          label: 'Notif. Agen.',
          dataKey: 'notificada_agenda',
          width: 150,
          align: 'center',
          sortable: true,
        },
        {
          label: 'Notif. Comp.',
          dataKey: 'notificada_completa',
          width: 150,
          align: 'center',
          sortable: true,
        },
      ].map(col => ({ ...col, showOverflowTooltip: true }));
    },

    allTableRowsSelected() {
      return this.tableData.reduce(
        (acc, visit) =>
          (acc ? this.selectedTableRows.includes(visit.id) : false),
        true
      );
    },

    tableFooterButtonType() {
      if (!this.tableHasSelectedRows) {
        return '';
      }

      const selectedTableRowCount = this.selectedTableRows.length;

      const notificadaAgendadaRows = this.tableData.filter(
        ({ id, notificada_agenda: notifAgenda }) =>
          this.selectedTableRows.includes(id) && !notifAgenda
      );

      const completeSelectedRows = this.tableData.filter(
        ({ id, completa, notificada_completa: notifCompleta }) =>
          this.selectedTableRows.includes(id) && completa && !notifCompleta
      );

      if (selectedTableRowCount === completeSelectedRows.length) {
        return 'completada';
      } else if (
        completeSelectedRows.length === 0 &&
        selectedTableRowCount === notificadaAgendadaRows.length
      ) {
        return 'agendada';
      }

      return '';
    },

    tableFooterButtonLabel() {
      if (this.tableFooterButtonType === 'completada') {
        return 'Notificar completada';
      } else if (this.tableFooterButtonType === 'agendada') {
        return 'Notificar agendada';
      }

      return '';
    },

    showTableFooterButton() {
      return this.tableFooterButtonType !== '';
    },
  },

  components: {
    LoadingIndicator,
    PageWrap,
  },
};
</script>

<style lang="scss" scoped>
.btn-pendientes {
  margin-left: 0.5em;
}
</style>
